import { Extension } from '@tiptap/react';
import { Plugin } from '@tiptap/pm/state';

/**
 * Parses a roman number string into a number
 * Example: a -> 1, ab -> 28, ...
 */
export const parseRomanNumber = (roman: string): number => {
  const romanNumerals: Record<string, number> = {
    I: 1,
    V: 5,
    X: 10,
    L: 50,
    C: 100,
    D: 500,
    M: 1000,
  };

  return roman
    .toUpperCase()
    .split('')
    .reverse()
    .reduce((acc, char, idx, arr) => {
      const value = romanNumerals[char];
      const nextValue = romanNumerals[arr[idx - 1]];

      return value < nextValue ? acc - value : acc + value;
    }, 0);
};

/**
 * Parses a list item index of letters.
 * Example: a -> 1, ab -> 28, ...
 */
export const parseLetterNumber = (str: string): number => {
  const alphaVal = (char: string): number => char.toLowerCase().charCodeAt(0) - 97 + 1;

  return [...str].reverse().reduce((acc, char, idx) => {
    return acc + alphaVal(char) * Math.pow(26, idx);
  }, 0);
};

export const transformMsoStyles = (html: string): string => {
  html = html.replace(/<o:p>.*?<\/o:p>/g, '');

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  doc.querySelectorAll(`[style*="mso-"]`).forEach((node) => {
    const styles = parseStyleAttribute(node);
    const newStyles = [];
    for (const prop of Object.keys(styles)) {
      if (prop && !prop.startsWith('mso-')) {
        newStyles.push(`${prop}: ${styles[prop]}`);
      }
    }
    node.setAttribute('style', newStyles.join(';'));
  });

  doc.querySelectorAll('[style*="color: black"]').forEach((node) => {
    (node as HTMLElement).style.removeProperty('color');
  });

  return doc.documentElement.outerHTML;
};

export const transformLists = (html: string): string => {
  if (html.indexOf('mso-list:') === -1) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  let listStack: HTMLElement[] = [];
  let currentListId: string | undefined;
  const listElements = doc.querySelectorAll('p[style*="mso-list:"]');
  listElements.forEach((node) => {
    const el = <HTMLElement>node;
    const hasNonListItemSibling =
      !el.previousElementSibling || !(el.previousElementSibling.nodeName === 'OL' || el.previousElementSibling.nodeName === 'UL');

    // Parse 'mso-list' style attribute
    const styleAttribute = parseStyleAttribute(el);
    const msoListValue: string = styleAttribute['mso-list'];
    const alignment = styleAttribute['text-align'];
    const msoListInfos = msoListValue.split(' ');
    const msoListId = msoListInfos.find((e) => /l[0-9]+/.test(e));
    const msoListLevel = Number(msoListInfos.find((e: string) => e.startsWith('level'))?.substring(5) || 1);

    // Check for start of a new list
    if (currentListId !== msoListId && (hasNonListItemSibling || msoListLevel === 1)) {
      currentListId = msoListId;
      listStack = [];
    }

    while (msoListLevel > listStack.length) {
      const newList = createListElement(el);

      if (listStack.length > 0) {
        listStack[listStack.length - 1].appendChild(newList);
      } else {
        el.before(newList);
      }
      listStack.push(newList);
    }

    while (msoListLevel < listStack.length) {
      listStack.pop();
    }

    // Remove list item numbers and create li
    const li = document.createElement('li');
    if (alignment) {
      li.style.textAlign = alignment;
    }
    li.innerHTML = el.innerHTML.replace(listTypeRegex, '').trim();
    listStack[listStack.length - 1].appendChild(li);
    el.remove();
  });

  return doc.documentElement.outerHTML;
};

const listTypeRegex = /<!--\[if \!supportLists\]-->.*?<!--\[endif\]-->|<o:p>|<\/o:p>/gs;

const getListPrefix = (el: HTMLElement) => {
  const matches = el.innerHTML.match(listTypeRegex);
  if (matches?.length) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(matches[0], 'text/html');
    return doc.body.querySelector('span')?.textContent ?? '';
  }

  return '';
};

const parseStyleAttribute = (el: Element): { [prop: string]: string } => {
  // @ts-ignore
  const styleRaw: string = el?.attributes['style']?.value || '';
  return Object.fromEntries(styleRaw.split(`;`).map((line) => line.split(`:`).map((v) => v.trim())));
};

const listOrderRegex = {
  number: /[0-9]+\./,
  romanLower: /(?=[mdclxvi])m*(c[md]|d?c*)(x[cl]|l?x*)(i[xv]|v?i*)\./,
  romanUpper: /(?=[MDCLXVI])M*(C[MD]|D?C*)(X[CL]|L?X*)(I[XV]|V?I*)\./,
  letterLower: /[a-z]+\./,
  letterUpper: /[A-Z]+\./,
};

const createListElement = (el: HTMLElement, alignment?: string) => {
  const listInfo = getListInfo(getListPrefix(el));
  const list = document.createElement(listInfo.type);

  if (alignment) {
    list.style.textAlign = alignment;
  }

  if (listInfo.start > 1) {
    list.setAttribute('start', listInfo.start.toString());
  }

  return list;
};

export const getListInfo = (prefix: string) => {
  switch (true) {
    case listOrderRegex.number.test(prefix):
      return {
        type: 'ol',
        start: Number(prefix.match(listOrderRegex.number)?.[0]?.replace('.', '') ?? ''),
      };
    case listOrderRegex.romanLower.test(prefix):
    case listOrderRegex.romanUpper.test(prefix):
      return {
        type: 'ol',
        start: Number(parseRomanNumber(prefix.match(listOrderRegex.romanLower)?.[0].replace('.', '') ?? '')),
      };
    case listOrderRegex.letterLower.test(prefix):
    case listOrderRegex.letterUpper.test(prefix):
      return {
        type: 'ol',
        start: Number(parseLetterNumber(prefix.match(listOrderRegex.letterLower)?.[0].replace('.', '') ?? '')),
      };
  }

  return {
    type: 'ul',
    start: 1,
  };
};

export const MSOfficePaste = Extension.create({
  priority: 99999,
  name: 'msOfficePaste',

  addProseMirrorPlugins() {
    return [OfficePastePlugin];
  },
});

const OfficePastePlugin = new Plugin({
  props: {
    transformPastedHTML(html: string) {
      if (html.indexOf('microsoft-com') !== -1 && html.indexOf('office') !== -1) {
        html = transformLists(html);
        html = transformMsoStyles(html);
      }
      return html;
    },
  },
});
